import type {
  ExpenseAreaConfig,
  ExpenseAreaConfigResponse,
  ExpenseLineItem,
  ExpenseLineItemResponse,
  ExpenseScenarioDetailResponse,
} from '../types';
import {
  fromWireAmount,
  hasRequiredPercentBasis,
  isBuyerCommission,
  needsPercentBasis,
  toWireAmount,
} from './line-item-data';
import type { ExpenseScenarioFormData } from './types';

export type FormMode = 'create' | 'edit';

export const GLOBAL_SELECTION_ID = 'global';

/** Default geometry priority order for expense scenarios. */
export const DEFAULT_GEOMETRY_PRIORITY = [
  'County',
  'State',
  'Geology',
  'AOI',
  'Unit',
];

/** Blank form values used in create mode. */
export const DEFAULT_FORM_VALUES: ExpenseScenarioFormData = {
  name: '',
  effectiveDate: '',
  description: '',
  notes: '',
  geometryPriority: [...DEFAULT_GEOMETRY_PRIORITY],
  areas: [],
};

// ─────────────────────────────────────
// Configurable type mapping (form ⇄ backend)
// ─────────────────────────────────────

/**
 * Maps a frontend geometry type to the backend `configurable_type` string.
 * @param {string} geometryType - The frontend geometry type
 * @return {string} The backend configurable_type
 */
export function toConfigurableType(
  geometryType: ExpenseAreaConfig['geometryType']
): string {
  switch (geometryType) {
    case 'area':
      return 'AreaGeometry';
    case 'unit':
      return 'DevelopmentArea';
    case 'state':
      return 'State';
    case 'county':
      return 'County';
  }
}

/**
 * Maps a backend `configurable_type` to the frontend geometry type.
 * @param {string} configurableType - The backend configurable_type
 * @return {string} The frontend geometry type
 */
function fromConfigurableType(
  configurableType: string
): ExpenseAreaConfig['geometryType'] {
  switch (configurableType) {
    case 'DevelopmentArea':
      return 'unit';
    case 'State':
      return 'state';
    case 'County':
      return 'county';
    default:
      return 'area';
  }
}

// ─────────────────────────────────────
// Response mapper: Backend → Form (camelCase)
// ─────────────────────────────────────

/**
 * Maps a backend line item response into the editor's ExpenseLineItem shape.
 * The BE splits recurrence into `recurringOccurrences` + `isContinuous`; the
 * form folds those into a single `recurringOccurrences: number | 'continuous'`.
 * @param {ExpenseLineItemResponse} li - The line item from the API response
 * @return {ExpenseLineItem} The form line item
 */
function mapLineItemResponse(li: ExpenseLineItemResponse): ExpenseLineItem {
  return {
    id: li.id,
    expenseClass: li.expenseClass,
    subtype: li.subtype,
    context: li.context ?? undefined,
    description: li.description ?? '',
    amountType: li.amountType,
    amount: fromWireAmount(li.amount, li.amountType === 'percent'),
    percentBasis: li.percentBasis ?? undefined,
    targetAmountType: li.targetAmountType ?? undefined,
    targetAmount:
      fromWireAmount(li.targetAmount, li.targetAmountType === 'percent') ??
      undefined,
    maxAmountType: li.maxAmountType ?? undefined,
    maxAmount:
      fromWireAmount(li.maxAmount, li.maxAmountType === 'percent') ?? undefined,
    offsetMonths: li.offsetMonths ?? undefined,
    timingAnchor: li.timingAnchor ?? undefined,
    isRecurring: li.isRecurring,
    recurringIntervalMonths: li.recurringIntervalMonths ?? undefined,
    recurringOccurrences: li.isContinuous
      ? 'continuous'
      : (li.recurringOccurrences ?? undefined),
  };
}

/**
 * Maps a backend area config response into the editor's ExpenseAreaConfig.
 * Note: the show payload does not carry a unit's type, so a loaded "unit" area
 * has an empty `unitType` (the picker shows blank); saving still works because
 * the payload is keyed on `configurableId`.
 * @param {ExpenseAreaConfigResponse} ac - The area config from the API response
 * @return {ExpenseAreaConfig} The form area config
 */
function mapAreaConfigResponse(
  ac: ExpenseAreaConfigResponse
): ExpenseAreaConfig {
  const geometryType = fromConfigurableType(ac.configurableType);
  const isUnit = geometryType === 'unit';

  return {
    id: ac.id,
    configurableId: ac.configurableId,
    geometryType,
    category:
      geometryType === 'state'
        ? 'State'
        : geometryType === 'county'
          ? 'County'
          : (ac.configurableCategory ?? ''),
    areaName: isUnit ? '' : (ac.configurableDisplayName ?? ''),
    unitType: '',
    unitName: isUnit ? (ac.configurableDisplayName ?? '') : '',
    alias: ac.aliasName ?? '',
    notes: ac.notes ?? '',
    economicLimitApplied: ac.economicLimitApplied ?? false,
    // numeric comes back as a string from the JSON API; Number('') is 0, so
    // guard the empty case explicitly rather than coercing blindly.
    economicLimitAmount:
      ac.economicLimitAmount === null || ac.economicLimitAmount === undefined
        ? null
        : Number(ac.economicLimitAmount),
    economicLimitReactivate: ac.economicLimitReactivate ?? false,
    economicLimitReactivateMonths: ac.economicLimitReactivateMonths ?? null,
    fallbackAfeHeaderId: ac.fallbackAfeHeaderId ?? null,
    fallbackAfe: ac.fallbackAfe ?? null,
    lineItems: ac.lineItems.map(mapLineItemResponse),
  };
}

/**
 * Maps a detailed backend scenario response into form data and the map's
 * geometry cache (keyed by area-config id, for areas that carry a polygon).
 * @param {ExpenseScenarioDetailResponse} res - The scenario response
 * @return {{ formData: ExpenseScenarioFormData, geometries: Record<number, GeoJSON.Geometry> }}
 */
export function mapResponseToFormData(res: ExpenseScenarioDetailResponse): {
  formData: ExpenseScenarioFormData;
  geometries: Record<number, GeoJSON.Geometry>;
} {
  const geometries: Record<number, GeoJSON.Geometry> = {};

  const areas = res.areaConfigs.map((ac) => {
    if (ac.configurableGeojson) {
      const geojson = ac.configurableGeojson;
      geometries[ac.id] =
        'geometry' in geojson && geojson.geometry
          ? geojson.geometry
          : (geojson as unknown as GeoJSON.Geometry);
    }
    return mapAreaConfigResponse(ac);
  });

  return {
    formData: {
      name: res.name,
      effectiveDate: res.effectiveDate ?? '',
      description: res.description ?? '',
      notes: res.notes ?? '',
      geometryPriority:
        res.geometryPriority.length > 0
          ? res.geometryPriority
          : [...DEFAULT_GEOMETRY_PRIORITY],
      areas,
    },
    geometries,
  };
}

// ─────────────────────────────────────
// Payload builder: Form → Backend (snake_case nested attributes)
// ─────────────────────────────────────

/**
 * Maps a line item to the backend nested-attributes shape. Only includes `id`
 * for items that came from the server (in originalIds) so Rails creates new
 * ones. Buyer Commission rows carry the target/max pair instead of `amount`;
 * recurrence is split back into `recurring_occurrences` + `is_continuous`.
 * @param {ExpenseLineItem} li - The line item
 * @param {number[]} originalIds - Server-assigned IDs from the initial load
 * @return {Record<string, unknown>} Backend line item payload
 */
function mapLineItemToPayload(
  li: ExpenseLineItem,
  originalIds: number[]
): Record<string, unknown> {
  const isBc = isBuyerCommission(li);
  const recurring = li.isRecurring ?? false;
  const continuous = recurring && li.recurringOccurrences === 'continuous';

  const payload: Record<string, unknown> = {
    expense_class: li.expenseClass,
    subtype: li.subtype,
    context: li.context ?? null,
    description: li.description || null,
    amount_type: li.amountType,
    amount: isBc ? null : toWireAmount(li.amount, li.amountType === 'percent'),
    // Only a non-commission percent item carries a basis.
    percent_basis: needsPercentBasis(li) ? (li.percentBasis ?? null) : null,
    target_amount_type: isBc ? (li.targetAmountType ?? null) : null,
    target_amount: isBc
      ? toWireAmount(li.targetAmount, li.targetAmountType === 'percent')
      : null,
    max_amount_type: isBc ? (li.maxAmountType ?? null) : null,
    max_amount: isBc
      ? toWireAmount(li.maxAmount, li.maxAmountType === 'percent')
      : null,
    offset_months: li.offsetMonths ?? 0,
    // NULL is preserved, never resolved to a default. A row saved before this
    // field existed counts from the effective date today; stamping it with the
    // class default here would re-time it without the user touching the row.
    timing_anchor: li.timingAnchor ?? null,
    is_recurring: recurring,
    recurring_interval_months: recurring
      ? (li.recurringIntervalMonths ?? 1)
      : null,
    recurring_occurrences:
      recurring && !continuous && typeof li.recurringOccurrences === 'number'
        ? li.recurringOccurrences
        : null,
    is_continuous: continuous,
  };

  if (originalIds.includes(li.id)) payload.id = li.id;
  return payload;
}

/**
 * Maps an area to the backend nested-attributes shape for CREATE (no ids).
 * @param {ExpenseAreaConfig} area - The area config
 * @return {Record<string, unknown>} Backend area config payload
 */
/**
 * Area-level settings shared by the create and update payloads, so the two
 * paths cannot drift as fields are added.
 *
 * The server clears reactivation when the limit is off and clears the month
 * count when reactivation is off; sending the raw toggles keeps the client
 * from having to duplicate that rule.
 * @param {ExpenseAreaConfig} area - The area config
 * @return {Record<string, unknown>} The shared area attributes
 */
function areaSettingsPayload(area: ExpenseAreaConfig): Record<string, unknown> {
  return {
    alias_name: area.alias || null,
    notes: area.notes || null,
    economic_limit_applied: area.economicLimitApplied,
    economic_limit_amount: area.economicLimitAmount,
    economic_limit_reactivate: area.economicLimitReactivate,
    economic_limit_reactivate_months: area.economicLimitReactivateMonths,
    fallback_afe_header_id: area.fallbackAfeHeaderId,
  };
}

function mapNewAreaToPayload(area: ExpenseAreaConfig): Record<string, unknown> {
  return {
    configurable_type: toConfigurableType(area.geometryType),
    configurable_id: area.configurableId,
    ...areaSettingsPayload(area),
    line_items_attributes: area.lineItems.map((li) =>
      mapLineItemToPayload(li, [])
    ),
  };
}

/**
 * Builds the wrapped payload for creating a new scenario (POST).
 * @param {ExpenseScenarioFormData} values - The form values
 * @return {Record<string, unknown>} The wrapped payload
 */
export function buildCreatePayload(
  values: ExpenseScenarioFormData
): Record<string, unknown> {
  return {
    expense_scenario: {
      name: values.name,
      effective_date: values.effectiveDate || null,
      description: values.description || null,
      notes: values.notes || null,
      geometry_priority: values.geometryPriority,
      area_configs_attributes: values.areas.map(mapNewAreaToPayload),
    },
  };
}

/**
 * Builds a diff payload for updating an existing scenario (PATCH). Areas and
 * line items present at load keep their server ids so Rails updates them;
 * removed ones are sent as `{ id, _destroy: true }`; newly added ones omit ids
 * so Rails creates them. New areas are those whose id isn't in the loaded set.
 * @param {ExpenseScenarioFormData} values - Current form values
 * @param {ExpenseScenarioFormData} original - The values the form loaded with
 * @return {Record<string, unknown>} The wrapped payload
 */
export function buildUpdatePayload(
  values: ExpenseScenarioFormData,
  original: ExpenseScenarioFormData
): Record<string, unknown> {
  const originalAreaById = new Map(original.areas.map((a) => [a.id, a]));
  const currentAreaIds = new Set(values.areas.map((a) => a.id));
  const areaConfigsAttrs: Record<string, unknown>[] = [];

  // Areas removed since load.
  for (const oa of original.areas) {
    if (!currentAreaIds.has(oa.id)) {
      areaConfigsAttrs.push({ id: oa.id, _destroy: true });
    }
  }

  for (const area of values.areas) {
    const orig = originalAreaById.get(area.id);
    if (!orig) {
      areaConfigsAttrs.push(mapNewAreaToPayload(area));
      continue;
    }

    const origLineItemIds = orig.lineItems.map((li) => li.id);
    const lineItemsAttrs: Record<string, unknown>[] = area.lineItems.map((li) =>
      mapLineItemToPayload(li, origLineItemIds)
    );

    const currentLineItemIds = new Set(
      area.lineItems
        .filter((li) => origLineItemIds.includes(li.id))
        .map((li) => li.id)
    );
    for (const id of origLineItemIds) {
      if (!currentLineItemIds.has(id)) {
        lineItemsAttrs.push({ id, _destroy: true });
      }
    }

    areaConfigsAttrs.push({
      id: area.id,
      configurable_type: toConfigurableType(area.geometryType),
      configurable_id: area.configurableId,
      ...areaSettingsPayload(area),
      line_items_attributes: lineItemsAttrs,
    });
  }

  return {
    expense_scenario: {
      name: values.name,
      effective_date: values.effectiveDate || null,
      description: values.description || null,
      notes: values.notes || null,
      geometry_priority: values.geometryPriority,
      area_configs_attributes: areaConfigsAttrs,
    },
  };
}

// ─────────────────────────────────────
// Validation
// ─────────────────────────────────────

export const MISSING_AFE_AREA_ERROR =
  'Needs an AFE — its properties have no cost source without one';
export const MISSING_AFE_FORM_ERROR = 'Every area needs an AFE';

/**
 * Whether an area has its required fallback AFE.
 * @param {ExpenseAreaConfig} area - The area config
 * @return {boolean} True when an AFE is selected
 */
export function hasFallbackAfe(area: ExpenseAreaConfig): boolean {
  return !!area.fallbackAfeHeaderId;
}

/**
 * Whether an area config is complete: a real geometry selection, an AFE, and
 * no percentage line item missing its basis. The geometry check is anchored on
 * `configurableId` so it holds for every geometry type — including loaded
 * "unit" areas whose type the show payload omits.
 * @param {ExpenseAreaConfig} area - The area config
 * @return {boolean} True when the area is complete
 */
export function isAreaValid(area: ExpenseAreaConfig): boolean {
  if (area.configurableId <= 0) return false;
  // Every area needs an AFE. A slot that UWI-matches no AFE takes its area's,
  // and has no cost source at all without one — so the column is NOT NULL and
  // this blocks Save rather than letting the request 422.
  if (!hasFallbackAfe(area)) return false;
  // A percentage with no declared basis cannot be evaluated, so it blocks
  // Save rather than only showing the inline hint.
  return area.lineItems.every(hasRequiredPercentBasis);
}

/**
 * Whether every mandatory field on the form is filled: the scenario name plus
 * each area's selected geometry and AFE. Gates the Save button.
 * @param {ExpenseScenarioFormData} data - The current form data
 * @return {boolean} True when the scenario can be saved
 */
export function isExpenseScenarioValid(data: ExpenseScenarioFormData): boolean {
  if (data.name.trim().length === 0) return false;
  return data.areas.every(isAreaValid);
}
